import React, { useMemo, useState } from 'react';
import { Button, Card, Empty, Input, Modal, Space, Table, Tag, Typography, Upload } from 'antd';
import { DeleteOutlined, DownloadOutlined, EditOutlined, EyeOutlined, FilePdfOutlined, UploadOutlined } from '@ant-design/icons';
import { formatDateTime } from '../utils/formatters';

const { Text } = Typography;

export default function LeadFilesTab({
  clientId,
  files,
  onUploadFile,
  onDeleteFile,
  onEditFileDescription,
  onPreviewPDF,
  onDownloadFile,
  canDelete = true,
}) {
  const [pendingFile, setPendingFile] = useState(null);
  const [uploadDescription, setUploadDescription] = useState('');
  const [editingFileId, setEditingFileId] = useState(null);
  const [editingDescription, setEditingDescription] = useState('');

  const clientFiles = useMemo(
    () => files.filter((file) => file.clientId === clientId),
    [clientId, files],
  );

  const closeUploadModal = () => {
    setPendingFile(null);
    setUploadDescription('');
  };

  const columns = [
    {
      title: 'File Name',
      dataIndex: 'fileName',
      key: 'fileName',
      width: '25%',
      render: (text, record) => (
        <Space>
          <FilePdfOutlined style={{ fontSize: 18, color: '#ff4d4f' }} />
          <span>{text}</span>
          {record.generatedFromOffer && <Tag color="blue">Auto-generated</Tag>}
        </Space>
      ),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      width: '30%',
      render: (text, record) => {
        if (editingFileId === record.id) {
          return (
            <Space direction="vertical" style={{ width: '100%' }}>
              <Input.TextArea
                value={editingDescription}
                rows={2}
                onChange={(event) => setEditingDescription(event.target.value)}
              />
              <Space>
                <Button
                  type="primary"
                  size="small"
                  onClick={() => {
                    onEditFileDescription(record.id, editingDescription);
                    setEditingFileId(null);
                    setEditingDescription('');
                  }}
                >
                  Save
                </Button>
                <Button
                  size="small"
                  onClick={() => {
                    setEditingFileId(null);
                    setEditingDescription('');
                  }}
                >
                  Cancel
                </Button>
              </Space>
            </Space>
          );
        }
        return <Text>{text}</Text>;
      },
    },
    {
      title: 'Upload Date',
      dataIndex: 'uploadDate',
      key: 'uploadDate',
      width: '15%',
      render: (date) => formatDateTime(date),
    },
    {
      title: 'Uploaded By',
      dataIndex: 'uploadedBy',
      key: 'uploadedBy',
      width: '15%',
    },
    {
      title: 'Actions',
      key: 'actions',
      width: '15%',
      render: (_, record) => (
        <Space direction="vertical" size={2}>
          {record.fileType === 'pdf' && (
            <Button
              type="link"
              size="small"
              icon={<EyeOutlined />}
              style={{ padding: 0, height: 'auto' }}
              onClick={() => onPreviewPDF(record)}
              title="Preview PDF"
            >
              Preview
            </Button>
          )}
          <Button
            type="link"
            size="small"
            icon={<DownloadOutlined />}
            style={{ padding: 0, height: 'auto' }}
            onClick={() => onDownloadFile?.(record)}
            title="Download File"
          >
            Download
          </Button>
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            style={{ padding: 0, height: 'auto' }}
            onClick={() => {
              setEditingFileId(record.id);
              setEditingDescription(record.description || '');
            }}
            title="Edit Description"
          >
            Edit
          </Button>
          {canDelete && (
            <Button
              type="link"
              size="small"
              danger
              icon={<DeleteOutlined />}
              style={{ padding: 0, height: 'auto' }}
              onClick={() => onDeleteFile(record.id)}
              title="Delete File"
            >
              Delete
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <Card>
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <div style={{ marginBottom: 16 }}>
          <Upload
            beforeUpload={(file) => {
              setPendingFile(file);
              return false;
            }}
            showUploadList={false}
          >
            <Button icon={<UploadOutlined />}>Upload New File</Button>
          </Upload>
        </div>

        {clientFiles.length === 0 ? (
          <Empty description="No files available" image={Empty.PRESENTED_IMAGE_SIMPLE} />
        ) : (
          <Table dataSource={clientFiles} rowKey="id" pagination={false} bordered columns={columns} />
        )}
      </Space>

      <Modal
        title="Upload File"
        open={Boolean(pendingFile)}
        onCancel={closeUploadModal}
        onOk={() => {
          if (!pendingFile) return;
          onUploadFile(clientId, pendingFile, uploadDescription);
          closeUploadModal();
        }}
      >
        {pendingFile && (
          <div>
            <p>File: <strong>{pendingFile.name}</strong></p>
            <Input.TextArea
              placeholder="Enter file description..."
              rows={3}
              value={uploadDescription}
              onChange={(event) => setUploadDescription(event.target.value)}
            />
          </div>
        )}
      </Modal>
    </Card>
  );
}
